// Hand-rolled SVG chart primitives for the admin dashboard. No chart library —
// every primitive is a small, self-contained component that draws into a
// viewBox and scales with its container. Brand palette throughout; <title>
// elements give native hover tooltips; charts carry aria-labels and pair with
// legends/tables for non-visual access.

import type { ReactNode } from 'react';

// ---------- shared tokens ----------
export const SEV_COLOR = {
  critical: '#e11d48', // rose-600
  high:     '#f97316', // orange-500
  medium:   '#fbbf24', // amber-400
  low:      '#38bdf8'  // sky-400
} as const;

export const POSTURE_COLOR: Record<string, string> = {
  excellent: '#10b981', // emerald-500
  good:      '#0ea5e9', // sentinel-500
  fair:      '#f59e0b', // amber-500
  poor:      '#f43f5e'  // rose-500
};

export const POSTURE_ORDER = ['excellent', 'good', 'fair', 'poor'] as const;

/** Discrete brand ramp for a 0–10 security score. */
export function scoreColor(score: number | null | undefined): string {
  if (score == null) return '#94a3b8';   // slate-400
  if (score >= 8) return '#10b981';        // emerald-500
  if (score >= 6) return '#0ea5e9';        // sentinel-500
  if (score >= 4) return '#f59e0b';        // amber-500
  return '#f43f5e';                        // rose-500
}

export function scoreTextClass(score: number | null | undefined): string {
  if (score == null) return 'text-ink-3';
  if (score >= 8) return 'text-emerald-600';
  if (score >= 6) return 'text-accent';
  if (score >= 4) return 'text-amber-600';
  return 'text-rose-600';
}

// ---------- empty state ----------
export function ChartEmpty({ label = 'No data yet' }: { label?: string }) {
  return (
    <div className="flex h-32 items-center justify-center rounded-lg border border-dashed border-line bg-panel-2/60 text-xs font-medium text-ink-3">
      {label}
    </div>
  );
}

// ---------- KPI stat card ----------
export function StatCard({
  label, value, unit, delta, deltaSuffix = 'vs prev', tone = 'brand', icon, hint
}: {
  label: string;
  value: ReactNode;
  unit?: string;
  delta?: number | null;          // signed % change, null = no comparison
  deltaSuffix?: string;
  tone?: 'brand' | 'ok' | 'warn' | 'bad' | 'neutral';
  icon?: ReactNode;
  hint?: string;
}) {
  const valueColor = {
    brand:   'text-accent',
    ok:      'text-emerald-600',
    warn:    'text-amber-600',
    bad:     'text-rose-600',
    neutral: 'text-ink'
  }[tone];

  const up = (delta ?? 0) > 0;
  const flat = !delta || Math.abs(delta) < 0.5;

  return (
    <div className="panel relative overflow-hidden p-4 sm:p-5">
      <div className="flex items-start justify-between gap-2">
        <p className="text-[10px] font-bold uppercase tracking-wider text-ink-3">{label}</p>
        {icon && <span className="text-sentinel-500/70" aria-hidden="true">{icon}</span>}
      </div>
      <p className={`tnum mt-1.5 text-2xl font-bold leading-none sm:text-[28px] ${valueColor}`}>
        {value}
        {unit && <span className="ml-1 text-sm font-semibold text-ink-3">{unit}</span>}
      </p>
      <div className="mt-2 flex items-center gap-2 text-[11px]">
        {delta != null && (
          <span
            className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 font-semibold ${
              flat ? 'bg-panel-2 text-ink-3'
                : up ? 'bg-emerald-50 text-emerald-700'
                     : 'bg-rose-50 text-rose-700'
            }`}
          >
            {flat ? '±0%' : `${up ? '▲' : '▼'} ${Math.abs(delta).toFixed(0)}%`}
          </span>
        )}
        {hint
          ? <span className="text-ink-3">{hint}</span>
          : delta != null && <span className="text-ink-3">{deltaSuffix}</span>}
      </div>
    </div>
  );
}

// ---------- vertical column chart (volume / histogram) ----------
export function Columns({
  data, color = '#0ea5e9', height = 150, label, valueFormat = (v) => String(v)
}: {
  data: { key: string; value: number; color?: string; tip?: string }[];
  color?: string;
  height?: number;
  label?: string;
  valueFormat?: (v: number) => string;
}) {
  if (data.length === 0) return <ChartEmpty />;
  const W = 600, H = height, pad = 6, base = H - 16;
  const max = Math.max(1, ...data.map(d => d.value));
  const bw = (W - pad * 2) / data.length;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="none" role="img" aria-label={label}>
      {[0.25, 0.5, 0.75].map(g => (
        <line key={g} x1={0} x2={W} y1={base - base * g} y2={base - base * g} stroke="#e2e8f0" strokeWidth={1} strokeDasharray="3 4" />
      ))}
      {data.map((d, i) => {
        const h = (d.value / max) * (base - 6);
        const x = pad + i * bw;
        return (
          <rect
            key={d.key} x={x + 1} y={base - h} width={Math.max(1.5, bw - 2)} height={h}
            fill={d.color ?? color} rx={2} className="transition-opacity hover:opacity-75"
          >
            <title>{d.tip ?? `${d.key}: ${valueFormat(d.value)}`}</title>
          </rect>
        );
      })}
      <line x1={0} y1={base} x2={W} y2={base} stroke="#cbd5e1" strokeWidth={1} />
    </svg>
  );
}

// ---------- area + line trend ----------
export function AreaLine({
  points, color = '#0ea5e9', height = 160, label, yMax
}: {
  points: { key: string; value: number; tip?: string }[];
  color?: string;
  height?: number;
  label?: string;
  yMax?: number;
}) {
  if (points.length === 0) return <ChartEmpty />;
  const W = 600, H = height, padX = 4, base = H - 14, top = 8;
  const max = yMax ?? Math.max(1, ...points.map(p => p.value));
  const span = points.length > 1 ? points.length - 1 : 1;
  const xAt = (i: number) => padX + (i / span) * (W - padX * 2);
  const yAt = (v: number) => base - (v / max) * (base - top);

  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i).toFixed(1)} ${yAt(p.value).toFixed(1)}`).join(' ');
  const area = `${line} L ${xAt(points.length - 1).toFixed(1)} ${base} L ${xAt(0).toFixed(1)} ${base} Z`;
  const gid = `area-${label?.replace(/\W/g, '') ?? 'g'}`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="none" role="img" aria-label={label}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.28} />
          <stop offset="100%" stopColor={color} stopOpacity={0.02} />
        </linearGradient>
      </defs>
      {[0.33, 0.66].map(g => (
        <line key={g} x1={0} x2={W} y1={base - (base - top) * g} y2={base - (base - top) * g} stroke="#e2e8f0" strokeWidth={1} strokeDasharray="3 4" />
      ))}
      <path d={area} fill={`url(#${gid})`} />
      <path d={line} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      {points.map((p, i) => (
        <circle key={i} cx={xAt(i)} cy={yAt(p.value)} r={5} fill="transparent" className="hover:fill-white/0">
          <title>{p.tip ?? `${p.key}: ${p.value}`}</title>
        </circle>
      ))}
      <line x1={0} y1={base} x2={W} y2={base} stroke="#cbd5e1" strokeWidth={1} />
    </svg>
  );
}

// ---------- horizontal bar list (ranking / comparison) ----------
export function HBars({
  data, max, valueFormat = (v) => String(v), barClass = 'bg-sentinel-500', mono = false
}: {
  data: { label: string; value: number; color?: string; sub?: string }[];
  max?: number;
  valueFormat?: (v: number) => string;
  barClass?: string;
  mono?: boolean;
}) {
  if (data.length === 0) return <ChartEmpty />;
  const m = max ?? Math.max(1, ...data.map(d => d.value));
  return (
    <ul className="space-y-2">
      {data.map((d, i) => (
        <li key={`${d.label}-${i}`} className="text-sm">
          <div className="flex items-baseline justify-between gap-3">
            <span className={`truncate ${mono ? 'font-mono text-xs' : ''} text-ink-2`}>{d.label}</span>
            <span className="shrink-0 text-xs font-semibold text-ink-2">
              {valueFormat(d.value)}{d.sub && <span className="ml-1 font-normal text-ink-3">{d.sub}</span>}
            </span>
          </div>
          <div className="mt-1 h-2 overflow-hidden rounded-full bg-panel-2">
            <div
              className={`h-full rounded-full ${d.color ? '' : barClass}`}
              style={{ width: `${Math.max(2, (d.value / m) * 100)}%`, background: d.color }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

// ---------- donut (posture / categorical mix) ----------
export function Donut({
  data, size = 168, thickness = 22, centerLabel, centerValue
}: {
  data: { label: string; value: number; color: string }[];
  size?: number;
  thickness?: number;
  centerLabel?: string;
  centerValue?: ReactNode;
}) {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return <ChartEmpty label="No data yet" />;
  const r = (size - thickness) / 2;
  const C = 2 * Math.PI * r;
  let offset = 0;

  return (
    <div className="flex items-center gap-5">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0" role="img" aria-label={centerLabel}>
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#eef2f7" strokeWidth={thickness} />
          {data.map((d) => {
            const frac = d.value / total;
            const dash = frac * C;
            const seg = (
              <circle
                key={d.label} cx={size / 2} cy={size / 2} r={r} fill="none"
                stroke={d.color} strokeWidth={thickness}
                strokeDasharray={`${dash} ${C - dash}`} strokeDashoffset={-offset}
                className="transition-opacity hover:opacity-80"
              >
                <title>{d.label}: {d.value} ({Math.round(frac * 100)}%)</title>
              </circle>
            );
            offset += dash;
            return seg;
          })}
        </g>
        {(centerValue || centerLabel) && (
          <text x="50%" y="50%" textAnchor="middle" className="fill-slate-900">
            <tspan x="50%" dy="-0.1em" className="text-xl font-bold" style={{ fontSize: 24, fontWeight: 700 }}>{centerValue}</tspan>
            {centerLabel && <tspan x="50%" dy="1.4em" style={{ fontSize: 10, fill: '#94a3b8', textTransform: 'uppercase' }}>{centerLabel}</tspan>}
          </text>
        )}
      </svg>
      <ul className="min-w-0 space-y-1.5 text-sm">
        {data.map(d => (
          <li key={d.label} className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 shrink-0 rounded-xs" style={{ background: d.color }} aria-hidden="true" />
            <span className="capitalize text-ink-2">{d.label}</span>
            <span className="ml-auto tnum text-xs font-semibold text-ink-3">{d.value}</span>
            <span className="tnum w-9 text-right text-xs text-ink-3">{Math.round((d.value / total) * 100)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------- stacked severity bar ----------
export function SeverityBar({ counts }: { counts: { critical: number; high: number; medium: number; low: number } }) {
  const order: (keyof typeof counts)[] = ['critical', 'high', 'medium', 'low'];
  const total = order.reduce((s, k) => s + (counts[k] || 0), 0);
  if (total === 0) return <ChartEmpty label="No alerts recorded" />;
  return (
    <div>
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-panel-2">
        {order.map(k => {
          const v = counts[k] || 0;
          if (!v) return null;
          return (
            <div key={k} style={{ width: `${(v / total) * 100}%`, background: SEV_COLOR[k] }} title={`${k}: ${v}`} />
          );
        })}
      </div>
      <ul className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
        {order.map(k => (
          <li key={k} className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-xs" style={{ background: SEV_COLOR[k] }} aria-hidden="true" />
            <span className="text-xs capitalize text-ink-2">{k}</span>
            <span className="tnum ml-auto text-sm font-semibold text-ink">{counts[k] || 0}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------- hour × weekday heatmap ----------
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export function Heatmap({ data }: { data: { dow: number; hour: number; c: number }[] }) {
  if (data.length === 0) return <ChartEmpty label="No activity yet" />;
  const grid: number[][] = Array.from({ length: 7 }, () => new Array(24).fill(0));
  let max = 1;
  for (const d of data) {
    if (d.dow >= 0 && d.dow < 7 && d.hour >= 0 && d.hour < 24) {
      grid[d.dow][d.hour] = d.c;
      if (d.c > max) max = d.c;
    }
  }
  const shade = (v: number) => {
    if (v === 0) return '#f1f5f9'; // slate-100
    const t = 0.18 + 0.82 * (v / max);
    return `rgba(14, 165, 233, ${t.toFixed(3)})`; // sentinel-500 ramp
  };

  const cols24 = { display: 'grid', gridTemplateColumns: 'repeat(24, minmax(0, 1fr))', gap: '2px' } as const;
  return (
    <div className="overflow-x-auto">
      <div className="min-w-[560px]">
        <div className="flex">
          <div className="w-9 shrink-0" />
          <div className="flex-1" style={cols24}>
            {Array.from({ length: 24 }).map((_, h) => (
              <div key={h} className="text-center text-[8px] text-ink-3">{h % 3 === 0 ? h : ''}</div>
            ))}
          </div>
        </div>
        {grid.map((row, dow) => (
          <div key={dow} className="mt-[2px] flex items-center">
            <div className="w-9 shrink-0 pr-1 text-right text-[10px] font-medium text-ink-3">{DOW[dow]}</div>
            <div className="flex-1" style={cols24}>
              {row.map((v, h) => (
                <div
                  key={h}
                  className="aspect-square rounded-[2px]"
                  style={{ background: shade(v) }}
                  title={`${DOW[dow]} ${String(h).padStart(2, '0')}:00 — ${v} scan${v === 1 ? '' : 's'}`}
                />
              ))}
            </div>
          </div>
        ))}
        <div className="mt-2 flex items-center justify-end gap-1.5 text-[10px] text-ink-3">
          <span>less</span>
          {[0, 0.25, 0.5, 0.75, 1].map(t => (
            <span key={t} className="h-2.5 w-2.5 rounded-[2px]" style={{ background: t === 0 ? '#f1f5f9' : `rgba(14,165,233,${0.18 + 0.82 * t})` }} />
          ))}
          <span>more</span>
        </div>
      </div>
    </div>
  );
}

// ---------- sparkline (compact trend) ----------
export function Sparkline({ values, color = '#0ea5e9', width = 120, height = 32 }: {
  values: number[]; color?: string; width?: number; height?: number;
}) {
  if (values.length < 2) return <div className="h-8" />;
  const max = Math.max(1, ...values), min = Math.min(...values);
  const span = max - min || 1;
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * width;
    const y = height - 2 - ((v - min) / span) * (height - 4);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible" aria-hidden="true">
      <polyline points={pts} fill="none" stroke={color} strokeWidth={1.75} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}
